import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    type Pattern,
    PatternCandleHistogram,
    PatternReturnsChart,
    PatternVolatilityChart,
} from '@/entities/pattern';

import { DEFAULT_PATTERN_LAB_COLORS } from '@/shared/constants/color';
import { log } from '@/shared/lib/logging';
import { Card } from '@/shared/ui/card';

import { usePatternLabMode } from '../../model/selectors/pattern-lab-mode';
import type { PatternStudy } from '../../model/types/pattern-study';

import cls from './pattern-lab-charts.module.css';

type PatternStudyChartsProps = {
    patterns: PatternStudy[];
};

export const PatternLabCharts = memo(
    ({ patterns }: PatternStudyChartsProps) => {
        const { t } = useTranslation('pattern-studio');
        const mode = usePatternLabMode();

        log('patterns', patterns);

        const patternList = patterns.map((s) => {
            const c = s.color ?? s.pattern.color;

            return {
                ...s.pattern,
                ...(typeof c === 'string' && c.trim().length > 0
                    ? { color: c }
                    : {}),
                ...(s.name ? { pattern_name: s.name } : {}),
            };
        });

        const colorsById: Record<string, string> = Object.fromEntries(
            patterns.map((s, idx) => {
                const p = s.pattern as Partial<Pattern>;
                const id = String(
                    p._id ?? p.pattern_idx ?? p.pattern_name ?? 'pattern',
                );
                const color = (s.color ??
                    p.color ??
                    DEFAULT_PATTERN_LAB_COLORS[
                        idx % DEFAULT_PATTERN_LAB_COLORS.length
                    ]) as string;

                return [id, color];
            }),
        );

        const hasTimeToTarget = patterns.some(
            (s) => !!s.pattern.time_to_target_hist,
        );
        const hasTimeToFailure = patterns.some(
            (s) => !!s.pattern.time_to_failure_hist,
        );

        return (
            <Card
                helpId="PATTERN_LAB_CHARTS"
                isCollapsible
                isWithoutPadding
                title={t('patternStats.patternCharts')}
            >
                <div className={cls.root}>
                    <div
                        className={cls.item}
                        data-tour="pattern-lab-charts-returns"
                    >
                        <PatternReturnsChart
                            chartId="PATTERN_LAB_CHART_RETURNS"
                            colorsById={colorsById}
                            isAggregationMode={mode === 'aggregation'}
                            patterns={patternList}
                            title={t('patternStats.returns')}
                        />
                    </div>

                    <div
                        className={cls.item}
                        data-tour="pattern-lab-charts-volatility"
                    >
                        <PatternVolatilityChart
                            chartId="PATTERN_LAB_CHART_VOLATILITY"
                            colorsById={colorsById}
                            isAggregationMode={mode === 'aggregation'}
                            patterns={patternList}
                            title={t('patternStats.volatility')}
                        />
                    </div>

                    {hasTimeToTarget && (
                        <div className={cls.item}>
                            <Card
                                isWithoutPadding
                                title={t('patternStats.timeToTarget')}
                            >
                                <div style={{ padding: 16 }}>
                                    <PatternCandleHistogram
                                        height={250}
                                        items={patterns.map((s, i) => ({
                                            label:
                                                s.name ||
                                                s.pattern.unique_id ||
                                                `Pattern ${i + 1}`,
                                            ...(s.color
                                                ? { color: s.color }
                                                : {}),
                                            ...(s.pattern.time_to_target_hist
                                                ? {
                                                      series: s.pattern
                                                          .time_to_target_hist,
                                                  }
                                                : {}),
                                        }))}
                                        stats={patterns.map((s, i) => ({
                                            label:
                                                s.name ||
                                                s.pattern.unique_id ||
                                                `Pattern ${i + 1}`,
                                            probability70p:
                                                s.pattern.time_to_target_70p ??
                                                null,
                                            threshold:
                                                s.pattern
                                                    .time_to_target_threshold ??
                                                null,
                                            histogramData:
                                                s.pattern.time_to_target_hist,
                                        }))}
                                        variant="target"
                                    />
                                </div>
                            </Card>
                        </div>
                    )}

                    {hasTimeToFailure && (
                        <div className={cls.item}>
                            <Card
                                isWithoutPadding
                                title={t('patternStats.timeToFailure')}
                            >
                                <div style={{ padding: 16 }}>
                                    <PatternCandleHistogram
                                        height={250}
                                        items={patterns.map((s, i) => ({
                                            label:
                                                s.name ||
                                                s.pattern.unique_id ||
                                                `Pattern ${i + 1}`,
                                            ...(s.color
                                                ? { color: s.color }
                                                : {}),
                                            ...(s.pattern.time_to_failure_hist
                                                ? {
                                                      series: s.pattern
                                                          .time_to_failure_hist,
                                                  }
                                                : {}),
                                        }))}
                                        stats={patterns.map((s, i) => ({
                                            label:
                                                s.name ||
                                                s.pattern.unique_id ||
                                                `Pattern ${i + 1}`,
                                            probability70p:
                                                s.pattern.time_to_failure_70p ??
                                                null,
                                            threshold:
                                                s.pattern
                                                    .time_to_failure_threshold ??
                                                null,
                                            histogramData:
                                                s.pattern.time_to_failure_hist,
                                        }))}
                                        variant="failure"
                                    />
                                </div>
                            </Card>
                        </div>
                    )}
                </div>
            </Card>
        );
    },
);

PatternLabCharts.displayName = 'PatternStudyCharts';
